import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, type Variants } from 'framer-motion';
import { Check, ChevronDown, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  IconButton,
  Scroller,
} from '@/shared/ui';
import type { TicketListView } from '@/shared/api';
import { BoardViewIcon } from '@/shared/config';
import { cn } from '@/shared/lib/cn';
import { pluralRu } from '@/shared/lib/plural';
import { PRESET_META, revealSelected } from './filter-sets';
import styles from './TicketsList.module.css';

export interface ActiveFilterChip {
  key: string;
  label: string;
  Icon?: LucideIcon;
  clear: () => void;
}

export interface SetProps {
  views: TicketListView[];
  appliedId: string | null;
  canSave: boolean;
  onApply: (view: TicketListView) => void;
  onSave: () => void;
  onOverwrite: (view: TicketListView) => void;
  onRename: (view: TicketListView) => void;
  onRemove: (view: TicketListView) => void;
}

type Variant = 'tab' | 'chip';

function itemClass(variant: Variant, on: boolean) {
  return cn(
    variant === 'tab' ? styles.tabBtn : styles.setChip,
    variant === 'tab' && on && styles.tabBtnActive,
    variant === 'chip' && on && styles.setChipOn,
  );
}

function selectionProps(variant: Variant, on: boolean) {
  return variant === 'tab' ? { role: 'tab' as const, 'aria-selected': on } : { 'aria-pressed': on };
}

function setItems(p: SetProps, variant: Variant): React.ReactElement[] {
  const out: React.ReactElement[] = [];
  for (const v of p.views) {
    const on = v.id === p.appliedId;
    const meta = v.preset ? PRESET_META[v.preset] : undefined;
    const Icon = meta?.Icon ?? BoardViewIcon;
    const hint = [meta?.hint, on ? 'Щелчок — снять' : 'Правый клик — переименовать, обновить или удалить']
      .filter(Boolean)
      .join('. ');
    out.push(
      <ContextMenu key={v.id}>
        <ContextMenuTrigger
          render={
            <button
              type="button"
              {...selectionProps(variant, on)}
              title={hint}
              className={itemClass(variant, on)}
              onClick={() => p.onApply(v)}
            />
          }
        >
          <Icon size={14} aria-hidden />
          <span className={styles.setName}>{v.name}</span>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem disabled={on} onClick={() => p.onApply(v)}>
            <Check size={14} aria-hidden />
            Применить
          </ContextMenuItem>
          <ContextMenuItem disabled={on} onClick={() => p.onOverwrite(v)}>
            <Save size={14} aria-hidden />
            Сохранить текущие фильтры сюда
          </ContextMenuItem>
          <ContextMenuItem onClick={() => p.onRename(v)}>
            <Pencil size={14} aria-hidden />
            Переименовать…
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem variant="danger" onClick={() => p.onRemove(v)}>
            <Trash2 size={14} aria-hidden />
            Удалить
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>,
    );
  }

  if (p.canSave) {
    out.push(
      <IconButton
        key="add"
        label="Сохранить набор"
        tooltip="Сохранить вкладку и фильтры своим набором"
        className={variant === 'tab' ? styles.tabAdd : styles.setChipAdd}
        onClick={p.onSave}
      >
        <Plus size={16} aria-hidden />
      </IconButton>,
    );
  }
  return out;
}

export const SetTabs: React.FC<SetProps> = (props) => <>{setItems(props, 'tab')}</>;

function filterChipItems(chips: ActiveFilterChip[]): React.ReactElement[] {
  return chips.map(({ key, label, Icon, clear }) => (
    <button
      key={`f:${key}`}
      type="button"
      className={styles.filterChip}
      aria-label={`Снять фильтр: ${label}`}
      onClick={clear}
    >
      {Icon && <Icon size={14} aria-hidden className={styles.filterChipIcon} />}
      <span className={styles.filterChipLabel}>{label}</span>
      <X size={12} aria-hidden />
    </button>
  ));
}

const OPEN_KEY = 'workspace:sets-row-open';

function readOpen(): boolean {
  try {
    return window.localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeOpen(open: boolean): void {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
  }
}

const fan: Variants = {
  hidden: { opacity: 0, x: -12 },
  shown: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 6) * 0.025 },
  }),
  gone: { opacity: 0, transition: { duration: 0.1 } },
};

const calmFan: Variants = {
  hidden: { opacity: 0 },
  shown: { opacity: 1, transition: { duration: 0.15 } },
  gone: { opacity: 0, transition: { duration: 0.1 } },
};

export const MobileSetsRow: React.FC<SetProps & { chips: ActiveFilterChip[] }> = ({ chips, ...props }) => {
  const [open, setOpen] = useState(readOpen);
  const calm = useReducedMotion();
  const variants = calm ? calmFan : fan;

  const toggle = () => {
    setOpen(!open);
    writeOpen(!open);
  };
  const viewportRef = useRef<HTMLDivElement>(null);
  const hasViews = props.views.length > 0;
  useLayoutEffect(() => {
    if (open && hasViews) revealSelected(viewportRef.current);
  }, [open, hasViews]);
  useEffect(() => {
    if (open && hasViews) revealSelected(viewportRef.current);
  }, [open, hasViews]);
  const applied = props.views.find((x) => x.id === props.appliedId)?.name;
  const setsCount = props.views.length;
  const summary = [
    applied ?? `Наборы ${setsCount}`,
    chips.length > 0 ? `${chips.length} ${pluralRu(chips.length, 'фильтр', 'фильтра', 'фильтров')}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const items = setItems(props, 'chip');
  const filters = filterChipItems(chips);
  const row = [
    ...items,
    ...(filters.length > 0 ? [<span key="divider" className={styles.tabsDivider} aria-hidden />] : []),
    ...filters,
  ];

  return (
    <Scroller axis="x" className={styles.mobileSets} viewportClassName={styles.chipsRow} viewportRef={viewportRef}>
      { }
      <button
        type="button"
        className={cn(styles.setChip, styles.setsToggle, !open && applied && styles.setChipOn)}
        aria-expanded={open}
        aria-label={open ? 'Свернуть наборы' : `Наборы: ${summary}. Развернуть`}
        onClick={toggle}
      >
        {!open && <BoardViewIcon size={14} aria-hidden />}
        {!open && <span className={styles.setName}>{summary}</span>}
        <motion.span
          className={styles.setsChevron}
          animate={{ rotate: open ? 90 : 0 }}
          transition={calm ? { duration: 0 } : { type: 'spring', stiffness: 500, damping: 28 }}
        >
          <ChevronDown size={14} aria-hidden />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open &&
          row.map((node, i) => (
            <motion.div
              key={node.key ?? i}
              className={styles.fanItem}
              custom={i}
              variants={variants}
              initial="hidden"
              animate="shown"
              exit="gone"
            >
              {node}
            </motion.div>
          ))}
      </AnimatePresence>
    </Scroller>
  );
};

export const ActiveFiltersBar: React.FC<{ chips: ActiveFilterChip[] }> = ({ chips }) => {
  if (chips.length === 0) return null;
  return (
    <Scroller
      axis="x"
      className={styles.activeFilters}
      viewportClassName={styles.chipsRow}
      aria-label="Активные фильтры"
    >
      {filterChipItems(chips)}
    </Scroller>
  );
};
